import {
  useHeader,
  useHeaderBack,
  useHeaderBackNavigate,
} from "@Light/components/page/header/context";
import { useScaffold } from "@Light/scaffold";
import { clsx } from "clsx";
import React from "react";

export type PageBodyStepper = {
  current: number;
  last: number;
};

export type PageBodyProps = Omit<React.ComponentProps<"div">, "title"> & {
  backLink?: string;
  onBack?: () => void;
  notification?: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }> | React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  footer?: React.ReactNode;
  centered?: boolean;
  /**
   * Centers the title and subtitle horizontally. Independent of `centered`,
   * which centers the content block vertically.
   */
  centerText?: boolean;
  /**
   * If true, footer appears immediately after content instead of at bottom of screen.
   * Use for pages with forms/inputs where keyboard might cover fixed footer.
   */
  footerAfterContent?: boolean;
  stepper?: PageBodyStepper;
  /** Affects copy size and spacing, defaults to `md` */
  size?: "md" | "lg";
};

export const PageBody: React.FC<PageBodyProps> = React.forwardRef<
  HTMLDivElement,
  PageBodyProps
>(
  (
    {
      backLink,
      onBack,
      notification,
      icon: Icon,
      title,
      stepper,
      subtitle,
      footer,
      centered = false,
      centerText = false,
      footerAfterContent = false,
      size = "md",
      children,
      className,
    },
    ref,
  ) => {
    const { sizeProps } = useHeader();
    const scaffold = useScaffold();
    const Title = scaffold.page.title;
    const Subtitle = scaffold.page.subtitle;
    const { isMinimalHeader } = scaffold.page.header;
    const isLarge = size === "lg";

    useHeaderBack(onBack);
    useHeaderBackNavigate(onBack ? undefined : backLink);

    return (
      <div
        {...sizeProps}
        ref={ref}
        className={clsx("flex flex-col h-full", className)}
      >
        <div className="px-6 h-full overflow-y-auto">
          {(() => {
            // Render the main content
            const content = (
              <>
                {notification || Icon || title || subtitle ? (
                  <div
                    className={clsx(
                      "flex flex-col",
                      isLarge ? "gap-3xl" : "gap-4",
                      !centered && isMinimalHeader && "pb-4",
                      !centered && !isMinimalHeader && "pt-4",
                    )}
                  >
                    {notification}
                    {typeof Icon === "function" ? (
                      <div className="flex w-full items-center justify-center pb-6">
                        <div className="flex justify-center items-center rounded-full bg-success-25 p-3 shrink-0">
                          <Icon className="size-10 text-success-700 shrink-0" />
                        </div>
                      </div>
                    ) : (
                      Icon
                    )}
                    <div
                      className={clsx(
                        "flex flex-col",
                        isLarge && !centerText ? "gap-lg" : "gap-1",
                        centerText && "text-center",
                      )}
                    >
                      {stepper && (
                        <div className="text-secondary text-xs">
                          Step {stepper.current} of {stepper.last}
                        </div>
                      )}
                      {title ? <Title size={size}>{title}</Title> : null}
                      {subtitle ? (
                        <Subtitle size={size}>{subtitle}</Subtitle>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                {children}
              </>
            );

            // Render footer
            const footerElement = footer ? (
              <div
                className={clsx(
                  centered && "pt-6",
                  !centered && !footerAfterContent && "mt-auto",
                )}
              >
                {footer}
              </div>
            ) : null;

            // Return the appropriate layout
            if (centered && !footerAfterContent) {
              // Centered content with footer at bottom
              return (
                <div className="flex flex-col min-h-full pb-6">
                  <div
                    className={clsx(
                      "flex flex-col flex-1 justify-center",
                      isLarge ? "gap-4xl" : "gap-4",
                    )}
                  >
                    {content}
                  </div>
                  {footerElement}
                </div>
              );
            } else {
              // Non-centered or centered with footer after content
              return (
                <div
                  className={clsx(
                    "flex flex-col pt-2 pb-6",
                    isLarge ? "gap-4xl" : centered ? "gap-4" : "gap-6",
                    !centered && "min-h-full",
                  )}
                >
                  {content}
                  {footerElement}
                </div>
              );
            }
          })()}
        </div>
      </div>
    );
  },
);
